import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useDancerAttendance } from '@/data/sessions';
import { summarizeAttendance } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useVideoEnabled } from '@/data/videoStorage';
import { Badge } from '@/components/ui/Badge';
import type { Dancer } from '@pirouette/shared';

export const Route = createFileRoute('/parent/dancers/$id')({
  component: ParentDancerDetail,
});

function useDancer(id: string) {
  return useQuery({
    queryKey: ['parent-dancer', id],
    queryFn: async (): Promise<Dancer | null> => {
      const { data, error } = await supabase
        .from('dancers')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data as Dancer | null;
    },
  });
}

function ParentDancerDetail() {
  const { id } = Route.useParams();
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, '');
  const isVideosChild = currentPath === `/parent/dancers/${id}/videos`;
  const videoEnabled = useVideoEnabled();
  const dancer = useDancer(id);
  const attendance = useDancerAttendance(id);

  if (isVideosChild) {
    return <Outlet />;
  }

  if (dancer.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!dancer.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Dancer not found.
      </p>
    );
  }

  const d = dancer.data;
  const summary = summarizeAttendance(attendance.data ?? []);

  return (
    <div className="space-y-10">
      <div>
        <Link
          to="/parent/dancers"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Dancers
        </Link>
        <h1 className="mt-2">
          {d.preferred_name ?? d.first_name} {d.last_name}
        </h1>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          Born {d.date_of_birth}
          {d.pronouns ? ` · ${d.pronouns}` : ''}
        </p>
        {videoEnabled.data && (
          <nav aria-label="Dancer detail navigation" className="mt-5 flex gap-2">
            <Link
              to="/parent/dancers/$id/videos"
              params={{ id }}
              className="rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 py-2 text-[var(--text-sm)] hover:bg-[var(--color-surface-sunken)]"
            >
              Videos
            </Link>
          </nav>
        )}
      </div>

      <section aria-labelledby="attend-summary" className="surface-elevated p-6">
        <h2 id="attend-summary" className="font-medium text-[var(--text-lg)]">
          Attendance · all-time
        </h2>
        <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Stat label="Present" value={summary.present} />
          <Stat label="Late" value={summary.late} />
          <Stat label="Excused" value={summary.excused} />
          <Stat label="Absent" value={summary.absent} />
          <Stat
            label="Rate"
            value={summary.total === 0 ? '—' : `${Math.round(summary.rate * 100)}%`}
          />
        </dl>

        {attendance.data && attendance.data.length > 0 && (
          <details className="mt-6">
            <summary className="cursor-pointer text-[var(--color-accent)] text-[var(--text-sm)]">
              Full attendance history ({attendance.data.length} records)
            </summary>
            <ul className="mt-3 space-y-1 text-[var(--text-sm)]">
              {attendance.data.slice(0, 50).map((a) => (
                <li
                  key={a.id}
                  className="flex justify-between border-b border-[var(--color-border)] py-1"
                >
                  <span className="text-[var(--color-ink-muted)] tabular-nums">
                    {/* Marked-on date. Sprint 3 will join class_sessions to
                        show session_date instead; for now the create
                        timestamp is the closest signal available. */}
                    {a.created_at.slice(0, 10)}
                  </span>
                  <Badge
                    tone={
                      a.status === 'present'
                        ? 'success'
                        : a.status === 'late'
                          ? 'warn'
                          : a.status === 'excused'
                            ? 'info'
                            : 'danger'
                    }
                  >
                    {a.status}
                  </Badge>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <dt className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
        {label}
      </dt>
      <dd className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
        {value}
      </dd>
    </div>
  );
}
